import { useQuery } from 'react-query'
import toastr from 'react-hot-toast'
import { fetchWrapper } from 'utils/fetchWrapper'
import { INFINITE_CACHE_AND_STALE_TIME } from 'constants/index'
import type { DropdownOption } from 'types/common/DropdownOption'
import React from 'react'
import type {
  AccountRoleResponse,
  AccountRole,
} from 'types/entityTypes/AccountRole'
import { generateURL } from 'utilities/hooks/urlGeneration'
import { SPARK_RESOURCE_TYPES } from 'constants/common'

const getAccountRoleTypes = () => {
  const url = generateURL(SPARK_RESOURCE_TYPES.ACCOUNT_ROLES)
  return fetchWrapper<AccountRoleResponse>({
    url: url,
    method: 'GET',
  })
}

export function useAccountRoles() {
  const { data, ...rest } = useQuery('Account Role', getAccountRoleTypes, {
    onError: (error) => {
      if (error instanceof Error) {
        toastr.error(`Failed to fetch Account Role: ${error.message}`)
      }
    },
    ...INFINITE_CACHE_AND_STALE_TIME,
  })

  const { data: responseData, ...restData } = data || {}

  const idMap = responseData?.reduce<Record<string, AccountRole>>(
    (acc, curr) => {
      return { ...acc, [curr.id]: curr }
    },
    {}
  )

  const accountRoleIdByNameMap = React.useMemo(() => {
    return responseData?.reduce<Record<string, string>>((acc, curr) => {
      return { ...acc, [curr.attributes.name]: curr.id }
    }, {})
  }, [responseData])

  const accountRoleNameBytIdMap = React.useMemo(() => {
    return responseData?.reduce<Record<string, string>>((acc, curr) => {
      return { ...acc, [curr.id]: curr.attributes.name }
    }, {})
  }, [responseData])

  const dropdownOptions: DropdownOption[] =
    responseData?.map((type) => ({
      label: type.attributes.name,
      value: type.id,
    })) || []

  return {
    accountRole: responseData || [],
    accountRoleIdByNameMap,
    accountRoleNameBytIdMap,
    idMap,
    dropdownOptions,
    ...restData,
    ...rest,
  }
}
